import type { Groups } from "../../../../features/questionnaire";
import type { QuestionProps } from "../Question/types";

export type RenderItem = {
  primary: QuestionProps;
  children: QuestionProps[];
};

export type ErrorState = {
  hasError: boolean;
  errorMessage?: string;
};

export type GetErrorsParams = {
  primary: QuestionProps;
  children: QuestionProps[];
  errors?: Record<string, string>;
  visibilityMap?: Record<string, boolean>;
};

export const getErrors = ({
  primary,
  children,
  errors,
  visibilityMap,
}: GetErrorsParams): ErrorState => {
  if (errors?.[primary.name]) {
    return {
      hasError: true,
      errorMessage: errors[primary.name],
    };
  }

  const hasChildError = children.some(child => Boolean(errors?.[child.name]));

  if (!hasChildError) {
    return {
      hasError: false,
    };
  }

  const areAllChildrenHidden = !children.some(child =>
    visibilityMap ? visibilityMap[child.name] : child.visible,
  );

  if (areAllChildrenHidden) {
    return {
      hasError: true,
      errorMessage: primary.errorMessage,
    };
  }

  return {
    hasError: true,
  };
};

/**
 * Get the group info for a question if it's a primary question in a PersonalisedCoverQuestionGroup
 */
export const getGroupForQuestion = (name: string, groups: Groups) => {
  const group = groups[name];
  return ["personalised_cover", "shadow_personalised_cover"].includes(
    group?.kind,
  )
    ? group
    : undefined;
};

/**
 * Check if a question is a child question in any group
 */
export const isChildQuestion = (name: string, groups: Groups): boolean => {
  return Object.values(groups).some(group => group.names.includes(name));
};

type PrepareQuestionsForRenderParams = {
  questions: QuestionProps[];
  groups: Groups;
  visibilityMap: Record<string, boolean>;
};

/**
 * Transform flat questions array into renderable items with grouping info
 * @param questions - All questions (visibility is managed through visibilityMap)
 * @param groups - Group definitions for personalised cover questions
 * @param visibilityMap - Visibility state for questions
 */
export const prepareQuestionsForRender = ({
  questions,
  groups,
  visibilityMap,
}: PrepareQuestionsForRenderParams): RenderItem[] => {
  const renderedChildren = new Set<string>();
  const items: RenderItem[] = [];

  questions.forEach(question => {
    // Skip if already rendered as a child
    if (renderedChildren.has(question.name)) {
      return;
    }

    // Check visibility from the visibility map, falling back to the question's visible prop
    const visible = visibilityMap[question.name] ?? question.visible;

    // Don't render container if primary question is not visible
    // Question component handles its own visibility, but we need to prevent
    // rendering the empty container wrapper
    if (!visible) {
      return;
    }

    // Check if this is a primary question in a PersonalisedCoverQuestionGroup
    const group = getGroupForQuestion(question.name, groups);

    if (group) {
      // Find all child questions (includes hidden ones for error handling)
      const children = questions.filter(q => group.names.includes(q.name));
      children.forEach(child => renderedChildren.add(child.name));

      items.push({
        primary: question,
        children,
      });
    } else if (!isChildQuestion(question.name, groups)) {
      // Standalone question (no children)
      items.push({
        primary: question,
        children: [],
      });
    }
  });

  return items;
};
